/**
 * Import wizard at /operations/import — reimagined flow.
 *
 * State machine collapsed from 8 to 4 user-facing states:
 *   1. No session         → upload zone
 *   2. Has session, not   → "Process my data" button
 *      yet ingested          (calls /process — synchronous ~3s,
 *                             no AI, no rate limits, no timeouts)
 *   3. Ingested            → review panel + commit panel
 *   4. Committed           → "X rows committed" + start-new link
 *
 * AI is OPTIONAL. The actual import (parse → map → validate →
 * commit) requires zero AI calls. AI enhancements (better
 * mapping suggestions, quality detection) can land in a
 * separate optional flow later.
 *
 * Recovery: /process works from ANY prior state — uploaded,
 * understanding, awaiting_clarification, failed, etc. Click
 * Process to recover any stuck session.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  CommitPanel,
  FileUploadZone,
  ImportDashboard,
} from "../components/imports";
import { ImportPreviewTable } from "../components/imports/ImportPreviewTable";
import { PageHeader } from "../components/ui";
import { api } from "../lib/api";
import type { ImportSessionDTO } from "../lib/api";

export function ImportSessionPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <PageHeader
        title={t("operations.import_title_main")}
        subtitle={t("operations.import_title")}
        breadcrumbs={[
          { label: t("operations.import_breadcrumb_ops"), href: "/operations" },
          { label: t("operations.import_breadcrumb_import") },
        ]}
      />
      {sessionId ? (
        <_ActiveSession sessionId={sessionId} />
      ) : (
        <FileUploadZone />
      )}
    </div>
  );
}

function _ActiveSession({ sessionId }: { sessionId: string }) {
  const q = useQuery({
    queryKey: ["import-session", sessionId],
    queryFn: () => api.importGetSession(sessionId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5000;
      if (
        data.status === "completed"
        || data.status === "failed"
        || data.status === "abandoned"
      ) {
        return false;
      }
      if (data.status === "committing") return 2000;
      return 3000;
    },
  });

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  if (!q.data) return null;

  const session = q.data;
  return <_SessionBody session={session} sessionId={sessionId} />;
}

function _SessionBody({
  session,
  sessionId,
}: { session: ImportSessionDTO; sessionId: string }) {
  // The dashboard_state stamp can race with the row INSERTs in
  // /process. Use the rows endpoint as the source of truth for
  // "do we have ingested data to review/commit?"
  const rowsQ = useQuery({
    queryKey: ["import-rows-count", sessionId],
    queryFn: () => api.importListRows(sessionId, { status: "all", limit: 1 }),
    refetchInterval: 5000,
  });
  const totalIngested = rowsQ.data
    ? Object.values(rowsQ.data.totals).reduce((a, b) => a + (b as number), 0)
    : 0;
  const hasIngestedRows = totalIngested > 0;

  return (
    <div className="space-y-4">
      <ImportDashboard session={session} />
      <_ProcessCard session={session} />
      <ImportPreviewTable sessionId={session.id} />
      {hasIngestedRows && <CommitPanel session={session} />}
      {session.status === "completed" && hasIngestedRows && (
        <_CompletedSummary session={session} />
      )}
    </div>
  );
}

/**
 * The one big button. Calls /process which:
 * - Re-reads the file from R2
 * - Heuristic ingest (filename → entity, aliases → columns)
 * - Validates per row
 * - Persists ImportRow records
 * - Transitions session to reviewing_proposal
 *
 * Works from any session state — recovers stuck sessions too.
 */
function _ProcessCard({ session }: { session: ImportSessionDTO }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const process = useMutation({
    mutationFn: () => api.importProcess(session.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["import-session", session.id] });
      qc.invalidateQueries({ queryKey: ["import-rows", session.id] });
      qc.invalidateQueries({ queryKey: ["import-rows-count", session.id] });
    },
  });

  const isStuck =
    session.status === "understanding"
    || session.status === "awaiting_clarification"
    || session.status === "failed";

  return (
    <section className="rounded-xl border border-brand-blue/30 bg-ai-bg p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <Sparkles size={16} className="text-brand-blue" aria-hidden="true" />
            <h3 className="text-base font-semibold text-brand-deep">
              {isStuck
                ? t("operations.import_resume")
                : t("operations.import_ready_headline")}
            </h3>
          </div>
          <p className="mt-1 text-sm text-text-secondary">
            {isStuck
              ? t("operations.import_resume_body", { rows: session.total_rows_parsed })
              : t("operations.import_ready_body", {
                  files: session.total_files,
                  rows: session.total_rows_parsed,
                })}
          </p>
          {process.error && (
            <div className="mt-3 rounded-md border-2 border-rose-400 bg-rose-50 p-3">
              <p className="text-sm font-semibold text-rose-900">
                {t("operations.import_process_failed")}
              </p>
              <p className="mt-1 text-sm text-rose-800 break-all">
                {(process.error as Error).message}
              </p>
            </div>
          )}
        </div>
        <button
          type="button"
          disabled={process.isPending}
          onClick={() => {
            console.log(
              "[import/process] button onClick fired (build:rows-panel)",
            );
            process.mutate();
          }}
          className="inline-flex shrink-0 items-center gap-2 rounded-md bg-brand-blue px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {process.isPending ? (
            <Loader2
              size={14}
              className="motion-safe:animate-spin"
              aria-hidden="true"
            />
          ) : (
            <RefreshCw size={14} aria-hidden="true" />
          )}
          {process.isPending
            ? t("operations.import_processing")
            : t("operations.import_process_btn")}
        </button>
      </div>
    </section>
  );
}

function _CompletedSummary({ session }: { session: ImportSessionDTO }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
      <div className="flex items-start gap-3">
        <Check
          size={20}
          className="mt-0.5 shrink-0 text-emerald-700"
          aria-hidden="true"
        />
        <div>
          <h3 className="text-sm font-semibold text-emerald-900">
            {t("operations.import_complete_headline")}
          </h3>
          <p className="mt-1 text-sm text-emerald-800">
            {t("operations.import_complete_body", {
              committed: session.total_rows_committed,
            })}
            {session.total_rows_pending > 0 && (
              <>
                {" "}
                {t("operations.import_pending_suffix", {
                  pending: session.total_rows_pending,
                })}
                <a
                  href="/settings/imports"
                  className="underline"
                >
                  {t("operations.import_settings_link")}
                </a>
                {t("operations.import_pending_suffix_after")}
              </>
            )}
          </p>
        </div>
      </div>
    </section>
  );
}
